/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import Modal from '../../../Common/Components/Modal';
import WizardModal from '../../../Common/Components/WizardModal';
import { ReactComponent as SuccessIcon } from '../../../img/icons/success.svg';
import { AddNotificationProps } from '../../../types/AppContext';
import { EnergyReportPayload, Recipient } from '../../../types/EnergyReportPayload';
import { GroupReportWizardProps } from '../../../types/EnergyWizardProps';
import { LightsGroupsItem } from '../../../types/LightsGroupProps';
import { useAppContext } from '../../../utils/AppContext';
import { postRequest } from '../../../utils/fetch';
import Utils from '../../../utils/Utils';
import RecipientEditor from '../../../Common/Components/RecipientEditor';
import WizardGroupSelector from './WizardGroupSelector';
import WizardPeriodAndColumnSelector from './WizardPeriodAndColumnSelector';
import WizardSummary from '../../../Common/Components/WizardSummary';
import { httpOk } from '../../../utils/constants';

function EnergyReportWizard(props: GroupReportWizardProps): JSX.Element {
  const { lightingGroups, orgGroups, closeModal, selectedSite, selectedCustomer, site } = props;

  const { addNotification } = useAppContext();

  const [groupFilterActiveTab, setGroupFilterActiveTab] = useState<string>('site');
  const [mode, setMode] = useState<string>('AGGREGATED');
  const [hightlightedGroup, setHightlightedGroup] = useState<LightsGroupsItem>();
  const [reportName, setReportName] = useState<string>('');
  const [reportNameErrorActive, setReportNameErrorActive] = useState<boolean>(false);
  const [utilityBillingReference, setUtilityBillingReference] = useState<string>();
  const [doneWithSuccess, setDoneWithSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<DateTime>();
  const [endDate, setEndDate] = useState<DateTime>();
  const [granularity, setGranularity] = useState<string>('DAY');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const validateNodesPage = ({ displayError }: {displayError: boolean}) => {
    if (groupFilterActiveTab === 'site' || hightlightedGroup?.type?.includes(groupFilterActiveTab)) {
      return true;
    }
    if (displayError) {
      addNotification({ type: 'warning', message: `Please select an element from the ${groupFilterActiveTab.toLowerCase()} groups section.` });
    }
    return false;
  };

  const validateColumnsAndDatesPage = ({ displayError }: {displayError: boolean}) => {
    let notification: AddNotificationProps | undefined;
    if (startDate === undefined) {
      notification = { type: 'warning', message: 'Please specify start date.' };
    } else if (endDate === undefined) {
      notification = { type: 'warning', message: 'Please specify end date.' };
    } else if (selectedColumns.length === 0) {
      notification = { type: 'warning', message: 'Please select at least one column.' };
    }
    if (notification && displayError) {
      addNotification(notification);
    }
    return !notification;
  };

  const validateRecipientsPage = ({ displayError }: {displayError: boolean}) => {
    if (recipients.length !== 0) {
      return true;
    }
    if (displayError) {
      addNotification({ type: 'warning', message: 'Please select at least one recipient.' });
    }
    return false;
  };

  const validateSummaryPage = ({ displayError }: {displayError: boolean}) => {
    const reportNameMissing = !reportName || reportName.length === 0;
    if (displayError) {
      setReportNameErrorActive(reportNameMissing);
    }
    return !reportNameMissing;
  };

  async function sendEnergyReportRequest(payload: EnergyReportPayload) {
    let updatedPayload;
    if (payload.groupType === 'ORGANIZATIONAL') {
      updatedPayload = {
        active: payload.active,
        mode: payload.mode,
        siteId: payload.siteId,
        orgId: payload.orgId,
        groupId: payload.groupId,
        timePeriod: payload.timePeriod,
        reportName: payload.reportName,
        recipients: payload.recipients,
        outputColumns: payload.outputColumns,
        timeZone: payload.timeZone,
        granularity: payload.granularity,
        utilityBillingReference: payload.utilityBillingReference,
      };
    } else {
      updatedPayload = {
        active: payload.active,
        mode: payload.mode,
        siteId: payload.siteId,
        orgId: payload.orgId,
        lightingGroupId: payload.groupId,
        timePeriod: payload.timePeriod,
        reportName: payload.reportName,
        recipients: payload.recipients,
        outputColumns: payload.outputColumns,
        timeZone: payload.timeZone,
        granularity: payload.granularity,
        utilityBillingReference: payload.utilityBillingReference,
      };
    }
    try {
      setLoading(true);
      const { status, data, error } = await postRequest<EnergyReportPayload, { queryTaskId: string; }>(`/organizations/${selectedCustomer.id}/sites/${selectedSite.id}/energy/energy_report_email`, updatedPayload);
      if (status === httpOk && data?.queryTaskId) {
        setLoading(false);
        setDoneWithSuccess(true);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      setLoading(false);
      addNotification({ type: 'warning', message: `Could not fetch energy data: '${error}'` });
    }
  }

  const navNodes = 1;
  const navDate = 2;
  const navRecipients = 3;
  const navReviewRun = 4;

  const navItems = new Map([
    [navNodes, { className: '',
      label: '1',
      labelName: 'Nodes',
      validatorFn: validateNodesPage,
      content:
  <WizardGroupSelector
    hightlightedGroup={hightlightedGroup}
    setHightlightedGroup={setHightlightedGroup}
    orgGroups={orgGroups}
    lightingGroups={lightingGroups}
    selectedSite={selectedSite}
    groupFilterActiveTab={groupFilterActiveTab}
    setGroupFilterActiveTab={setGroupFilterActiveTab}
    granularity={mode}
    setGranularity={setMode}
  />,
    }],
    [navDate, { className: 'long-text',
      label: '2',
      labelName: 'Date range & output columns',
      validatorFn: validateColumnsAndDatesPage,
      content:
  <WizardPeriodAndColumnSelector
    startDate={startDate?.toJSDate()}
    setStartDate={(dt) => setStartDate(DateTime.fromJSDate(dt as Date))}
    endDate={endDate?.toJSDate()}
    setEndDate={(dt) => setEndDate(DateTime.fromJSDate(dt as Date))}
    granularity={granularity}
    setGranularity={setGranularity}
    selectedColumns={selectedColumns}
    setSelectedColumns={setSelectedColumns}
    mode={mode}
    nodeGroup={groupFilterActiveTab}
    allSelected={allSelected}
    setAllSelected={setAllSelected}
  />,
    }],
    [navRecipients, { className: '',
      label: '3',
      labelName: 'Recipients',
      validatorFn: validateRecipientsPage,
      content:
  <RecipientEditor
    recipients={recipients}
    setRecipients={setRecipients}
    selectedCustomer={selectedCustomer}
  /> }],
    [navReviewRun, { className: '',
      label: '4',
      labelName: 'Review & run',
      validatorFn: validateSummaryPage,
      content:
  <WizardSummary
    selectedGroup={hightlightedGroup}
    groupFilterActiveTab={groupFilterActiveTab}
    selectedSite={selectedSite}
    mode={mode}
    utilityReference={utilityBillingReference}
    setUtilityReference={setUtilityBillingReference}
    reportName={reportName}
    setReportName={setReportName}
    reportNameErrorActive={reportNameErrorActive}
    loading={loading}
    recipients={recipients}
    selectedColumns={selectedColumns}
    granularity={granularity}
    startDate={startDate as DateTime}
    endDate={endDate as DateTime}
  /> }],
  ]);

  return (
    <>
      {!doneWithSuccess && site !== undefined ? (
        <WizardModal
          title="Energy use report"
          closeModal={closeModal}
          submitButtonAction={() => {
            sendEnergyReportRequest({
              active: true,
              mode,
              siteId: selectedSite.id,
              orgId: selectedCustomer.id,
              groupId: hightlightedGroup?.groupId,
              groupType: hightlightedGroup?.type,
              timePeriod: Utils.calculateDateParams(granularity, startDate as DateTime, endDate as DateTime),
              reportName,
              recipients,
              outputColumns: selectedColumns,
              utilityBillingReference,
              timeZone: site?.time_zone,
              granularity,
            } as EnergyReportPayload);
          }}
          width="805"
          height="495"
          navItems={navItems}
        />
      ) : (
        <Modal
          setModalOpen={() => closeModal()}
          title="Success"
          primaryButtonAction={() => closeModal()}
          primaryButtonLabel="OK"
          width="354"
          height="276"
          className="success-modal"
        >
          <>
            <SuccessIcon />
            <div>
              <div>Your request has been submitted.</div>
              <div> Report generation may take several hours depending on amount of data requested. </div>
              <div> Recipients will receive email notification when the report is ready.</div>
            </div>
          </>
        </Modal>
      )}
    </>
  );
}

export default EnergyReportWizard;
